import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query'
import { z } from 'zod'
import { apiRequest } from '@/shared/api'
import { type CursorPage, nextCursor } from '@/shared/lib'
import { CompanyListItemSchema, CompanySchema } from '../model/types'

export const companyKeys = {
  all: ['company'] as const,
  list: (filters: CompanyListFilters = {}) => ['company', 'list', filters] as const,
  bySlug: (slug: string) => ['company', 'by-slug', slug] as const,
  detail: (id: string) => ['company', 'detail', id] as const,
}

// Фильтры справочника: оба флага прокидываются на бэк, где и применяются —
// фильтрация после выборки рвала бы курсорную пагинацию.
export interface CompanyListFilters {
  // Скрытые в deal компании (visibility service=deal).
  includeHidden?: boolean
  // Архивные компании (по умолчанию справочник их не отдаёт).
  includeArchived?: boolean
}

// Справочник компаний — курсорный листинг. Скрытые страница подсвечивает бейджем
// через diff двух наборов (CompanyListItem флага видимости не несёт); архивные
// приходят с флагом archived в самой строке.
export function companiesQueryOptions(filters: CompanyListFilters = {}) {
  const { includeHidden = false, includeArchived = false } = filters
  return infiniteQueryOptions({
    queryKey: companyKeys.list(filters),
    queryFn: async ({ pageParam }) => {
      const body = await apiRequest<CursorPage<unknown>>({
        method: 'GET',
        url: '/companies',
        query: {
          ...(pageParam ? { cursor: pageParam } : {}),
          ...(includeHidden ? { include_hidden: true } : {}),
          ...(includeArchived ? { include_archived: true } : {}),
          limit: 200,
        },
      })
      return {
        items: z.array(CompanyListItemSchema).parse(body.items),
        next_cursor: body.next_cursor ?? null,
      }
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: nextCursor,
  })
}

// Карточка по slug (человекочитаемый адрес). Бэк отдаёт by-id — mock резолвит
// by-slug (backend-хендофф в roadmap Backlog).
export function companyBySlugQueryOptions(slug: string) {
  return queryOptions({
    queryKey: companyKeys.bySlug(slug),
    queryFn: async () =>
      CompanySchema.parse(
        await apiRequest({ method: 'GET', url: '/companies/by-slug', query: { slug } }),
      ),
  })
}

// Карточка по id (контур «Моя компания» заказчика — company_id из session/me).
export function companyByIdQueryOptions(id: string) {
  return queryOptions({
    queryKey: companyKeys.detail(id),
    queryFn: async () =>
      CompanySchema.parse(await apiRequest({ method: 'GET', url: `/companies/${id}` })),
  })
}
